import React, { useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { api, formatApiError } from "../lib/api";
import { useLang } from "../lib/lang";
import { Mail, MapPin, Loader2, Check, Send } from "lucide-react";

const COPY = {
  title: { en: "Contact Us", kar: "ဆဲးကျိးပှၤ", my: "ဆက်သွယ်ရန်" },
  intro: {
    en: "For press inquiries, humanitarian coordination, or general communications, please reach out below.",
    kar: "သတးကျိၣ်တၢ်ဂ့ၢ်",
    my: "သတင်းမီဒီယာ စုံစမ်းမေးမြန်းမှုများ၊ လူသားချင်းစာနာမှု ပူးပေါင်းဆောင်ရွက်ရေး သို့မဟုတ် ယေဘုယျ ဆက်သွယ်မှုများအတွက် အောက်တွင် ဆက်သွယ်ပါ။",
  },
  name: { en: "Full name", kar: "မံၤ", my: "အမည်အပြည့်အစုံ" },
  email: { en: "Email", kar: "အံမ့ၣ်လ်", my: "အီးမေးလ်" },
  subject: { en: "Subject", kar: "ခိၣ်တီ", my: "အကြောင်းအရာ" },
  message: { en: "Message", kar: "တၢ်ကစီၣ်", my: "မက်ဆေ့ချ်" },
  cta: { en: "Send message", kar: "ဆှၢလီၤ", my: "ပို့ပါ" },
  thanks: {
    en: "Message received. We will respond as soon as possible.",
    kar: "ပတူၢ်လိာ်နတၢ်ကစီၣ်ဘၣ်ဆၢ။",
    my: "မက်ဆေ့ချ်ကို လက်ခံရရှိပါပြီ။ အမြန်ဆုံး တုံ့ပြန်ပေးပါမည်။",
  },
};

export default function ContactPage() {
  const { lang } = useLang();
  const t = (k) => COPY[k][lang] || COPY[k].en;
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  async function submit(e) {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || "Failed to send.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="App" data-testid="contact-page">
      <Header />
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="border-b border-slate-200 pb-6 mb-10">
          <div className="text-xs uppercase tracking-[0.3em] text-red-600 font-semibold mb-2">Communications</div>
          <h1 className="font-display text-5xl sm:text-6xl text-slate-900">{t("title")}</h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-1 space-y-8">
            <p className="text-slate-700 leading-relaxed">{t("intro")}</p>
            <div className="space-y-5">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-slate-900 text-white flex items-center justify-center flex-shrink-0">
                  <Mail className="w-4 h-4" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Press</div>
                  <div className="text-slate-900">press@kawthoolei.org</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-slate-900 text-white flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-4 h-4" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">Headquarters</div>
                  <div className="text-slate-900">General Headquarters, Kawthoolei</div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2">
            {done ? (
              <div className="bg-slate-50 border border-slate-200 p-10 flex items-start gap-4" data-testid="contact-success">
                <div className="w-12 h-12 bg-green-600 text-white flex items-center justify-center flex-shrink-0">
                  <Check className="w-6 h-6" />
                </div>
                <div>
                  <div className="font-display text-3xl text-slate-900 mb-2">Thank you</div>
                  <p className="text-slate-600">{t("thanks")}</p>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="border border-slate-200 p-8 bg-white space-y-5" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs uppercase tracking-[0.2em] font-semibold text-slate-700 mb-2">{t("name")}</label>
                    <input
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      data-testid="contact-name"
                      className="w-full border border-slate-300 px-3 py-2.5 focus:outline-none focus:border-slate-900"
                    />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-[0.2em] font-semibold text-slate-700 mb-2">{t("email")}</label>
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      data-testid="contact-email"
                      className="w-full border border-slate-300 px-3 py-2.5 focus:outline-none focus:border-slate-900"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.2em] font-semibold text-slate-700 mb-2">{t("subject")}</label>
                  <input
                    required
                    value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    data-testid="contact-subject"
                    className="w-full border border-slate-300 px-3 py-2.5 focus:outline-none focus:border-slate-900"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.2em] font-semibold text-slate-700 mb-2">{t("message")}</label>
                  <textarea
                    required
                    rows={6}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    data-testid="contact-message"
                    className="w-full border border-slate-300 px-3 py-2.5 focus:outline-none focus:border-slate-900 resize-y"
                  />
                </div>
                {error && (
                  <div className="text-sm text-red-600 border-l-4 border-red-600 bg-red-50 px-3 py-2" data-testid="contact-error">{error}</div>
                )}
                <button
                  type="submit"
                  disabled={busy}
                  data-testid="contact-submit"
                  className="bg-slate-900 text-white px-6 py-3 text-sm uppercase tracking-[0.2em] font-semibold hover:bg-red-600 transition flex items-center gap-2 disabled:opacity-60"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {t("cta")}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
